/**
 * Interactive Factory authoring agent for draft blueprints (REQ-AIS-113).
 * Same ConversationShell as Run; backend uses runAgentTurn + authoring tools.
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ConversationShell } from '@/components/ai-studio/conversation';
import type { ActiveConversationContext } from '@/types/aiStudio';
import type {
  AuthoringAgentPatch,
  AuthoringVersionPatch,
  StudioAgentArchetype,
  StudioComputeClass,
  StudioExecutionMode,
} from '@/types/aiStudioAgent';
import {
  AUTHORING_HINT_STEP_MS,
  AUTHORING_INPUT_EXAMPLE_PROMPTS,
} from './authoringInputExamplePrompts';
import { useAgentAuthoring } from './useAgentAuthoring';
import {
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_TITLE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';

interface AgentAuthoringInterviewProps {
  agentId: string;
  versionId: string;
  agentName: string;
  enabled: boolean;
  /** Planning workspace pane: h-12 header + borderless chat body inside parent pane chrome. */
  workspacePane?: boolean;
  onApplyAgentPatch: (patch: AuthoringAgentPatch) => void;
  onApplyVersionPatch: (patch: {
    contextDefinition?: string;
    successCriteria?: string;
    explainInstructions?: string;
    executionMode?: StudioExecutionMode;
    computeClasses?: StudioComputeClass[];
    archetype?: string | null;
    inputExpected?: AuthoringVersionPatch['inputExpected'];
    outputExpected?: AuthoringVersionPatch['outputExpected'];
  }) => void;
}

export const AgentAuthoringInterview: React.FC<AgentAuthoringInterviewProps> = ({
  agentId,
  versionId,
  agentName,
  enabled,
  workspacePane = false,
  onApplyAgentPatch,
  onApplyVersionPatch,
}) => {
  const { t } = useLanguage();

  const authoring = useAgentAuthoring(agentId, versionId, enabled, {
    onApplyAgentPatch,
    onApplyVersionPatch: (patch) =>
      onApplyVersionPatch({
        ...patch,
        archetype: patch.archetype as StudioAgentArchetype | null | undefined,
      }),
  });

  const context: ActiveConversationContext = useMemo(
    () => ({
      contextType: 'AGENT_AUTHORING',
      scopeLabel: agentName,
      status: 'open',
    }),
    [agentName]
  );

  if (!enabled) return null;

  const progressSummary = authoring.progress
    ? `${authoring.progress.items.filter((i) => i.complete).length}/${authoring.progress.items.length}`
    : null;

  const progressBadges = (
    <div className="flex flex-wrap items-center gap-2">
      {progressSummary && (
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 py-0.5 text-sm text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.authoring.progressTitle')}: {progressSummary}
        </span>
      )}
      {authoring.progress?.readyForTest && (
        <span className="rounded-full bg-[var(--color-success)] px-2.5 py-0.5 text-sm text-white">
          {t('aiStudio.authoring.readyForTest')}
        </span>
      )}
    </div>
  );

  const chatShell = authoring.loading ? (
    <p className="px-3 py-2 text-base text-[var(--color-surface-text-secondary)]">
      {t('aiStudio.definition.loading')}
    </p>
  ) : (
    <ConversationShell
      className="min-h-0 flex-1"
      context={context}
      messages={authoring.messages}
      sending={authoring.sending}
      error={authoring.errorKey ? t(authoring.errorKey) : null}
      onSend={(text) => void authoring.send(text)}
      hideContextIndicator
      paneMode={workspacePane}
      composerVariant="pill"
      composerFloating
      composerHints={AUTHORING_INPUT_EXAMPLE_PROMPTS}
      composerHintStepMs={AUTHORING_HINT_STEP_MS}
      composerPlaceholder={t('aiStudio.authoring.placeholder')}
    />
  );

  if (workspacePane) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <header className={ASSIST_PANE_HEADER_CLASS}>
          <h2 className={ASSIST_PANE_TITLE_CLASS} title={t('aiStudio.authoring.title')}>
            {t('aiStudio.authoring.title')}
          </h2>
          {progressBadges}
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
          {chatShell}
        </div>
      </div>
    );
  }

  return (
    <section className="flex h-full min-h-0 flex-col gap-2 overflow-hidden">
      <header className="flex flex-shrink-0 flex-wrap items-start justify-between gap-2 px-1">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.authoring.title')}
          </h2>
          <p className="mt-0.5 text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.authoring.subtitle')}
          </p>
        </div>
        {progressBadges}
      </header>
      {chatShell}
    </section>
  );
};
